import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, Download, Plug, Plus, Trash2 } from 'lucide-react';
import { api } from '../../../api/client';
import InfoTip from '../../../components/InfoTip';
import ErrorBanner from '../../../components/ErrorBanner';
import { formatRelative } from '../../../lib/format';
import { toast } from '../../../lib/toast';

export default function IntegrationsSection() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const onDownloadPlugin = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/api/wordpress-plugin/download', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rankonai.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('WordPress plugin downloaded');
    } catch (err) {
      toast.error(err, 'Could not download the plugin');
    } finally {
      setDownloading(false);
    }
  };

  const keysQuery = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get('/api/me/api-keys').then((r) => r.data.keys),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/api/me/api-keys', body).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      setJustCreated(data);
      setName('');
      setError('');
      toast.success("API key created — copy it now, you won't see it again");
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Could not create key');
      toast.error(err, 'Could not create key');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => api.post(`/api/me/api-keys/${id}/revoke`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key revoked');
    },
    onError: (err) => toast.error(err, 'Could not revoke key'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/me/api-keys/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key deleted');
    },
    onError: (err) => toast.error(err, 'Could not delete key'),
  });

  const onCopy = async () => {
    if (!justCreated?.plaintext) return;
    await navigator.clipboard.writeText(justCreated.plaintext);
    setCopied(true);
    toast.success('Key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const keys = keysQuery.data || [];

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Plug className="w-4 h-4" />
          </span>
          Integrations
          <InfoTip
            title="API keys + WordPress plugin"
            body="Long-lived bearer tokens (rai_sk_…) for server-to-server access, plus the WordPress plugin that uses them. Keys never expire and act with your account's permissions — treat them like passwords. Revoke immediately if leaked."
          />
        </div>
        <span className="text-xs text-ink-400">
          {keys.length} {keys.length === 1 ? 'key' : 'keys'}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-1">
        <Plug className="w-4 h-4 text-ink-400" />
        <label className="label !mb-0">API keys</label>
      </div>
      <p className="text-xs text-ink-400 mb-3">
        Long-lived bearer tokens for server-to-server access — used by the WordPress plugin,
        scripts, or any integration that needs to call this app&apos;s API. Treat them like
        passwords. You&apos;ll see the full key once, right after creating it.
      </p>

      {justCreated && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-4">
          <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            New key created — copy it now, you won&apos;t see it again
          </div>
          <div className="flex gap-2">
            <code className="input bg-white font-mono text-xs break-all flex-1">
              {justCreated.plaintext}
            </code>
            <button type="button" className="btn-secondary flex-none" onClick={onCopy}>
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Copy
                </>
              )}
            </button>
          </div>
          <button
            type="button"
            className="text-xs text-emerald-700 mt-3 underline"
            onClick={() => setJustCreated(null)}
          >
            I&apos;ve saved it — dismiss
          </button>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          createMutation.mutate({ name: name.trim() });
        }}
        className="flex gap-2 mb-4"
      >
        <input
          className="input flex-1"
          placeholder="Label (e.g. WordPress plugin — acme.com)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <button
          type="submit"
          className="btn-primary"
          disabled={createMutation.isPending || !name.trim()}
        >
          <Plus className="w-4 h-4" />
          {createMutation.isPending ? 'Creating…' : 'Generate key'}
        </button>
      </form>

      {error && (
        <div className="mb-3">
          <ErrorBanner body={error} onDismiss={() => setError('')} />
        </div>
      )}

      {keys.length === 0 ? (
        <p className="text-sm text-ink-400">No API keys yet.</p>
      ) : (
        <ul className="space-y-2">
          {keys.map((k) => {
            const revoked = !!k.revoked_at;
            return (
              <li key={k._id} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-ink-900 flex items-center gap-2 min-w-0 truncate">
                    <span className="truncate">{k.name}</span>
                    {revoked && (
                      <span className="pill bg-gray-100 text-gray-500 flex-none">Revoked</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-none">
                    {!revoked && (
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke "${k.name}"? Apps using it will stop working immediately.`,
                            )
                          ) {
                            revokeMutation.mutate(k._id);
                          }
                        }}
                        disabled={revokeMutation.isPending}
                      >
                        Revoke
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
                      onClick={() => {
                        if (window.confirm(`Permanently delete "${k.name}"?`)) {
                          deleteMutation.mutate(k._id);
                        }
                      }}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-xs text-ink-400 font-mono mt-1 break-words">
                  {k.prefix}… · created {formatRelative(k.createdAt)}
                  {k.last_used_at && ` · used ${formatRelative(k.last_used_at)}`}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="border-t border-gray-200 -mx-6 my-5" />

      <div className="flex items-center gap-2 mb-1">
        <Download className="w-4 h-4 text-ink-400" />
        <label className="label !mb-0">WordPress plugin</label>
        <InfoTip
          title="RankOnAI for WordPress"
          body="Install on a WordPress site to auto-inject schema, FAQs & llms.txt, control AI-bot access, and run scheduled AI rewrites. Connect it with an API key above to pull your audits, gaps & keywords into wp-admin."
        />
      </div>
      <p className="text-xs text-ink-400 mb-3">
        Download the plugin, then in WordPress go to <strong>Plugins → Add New → Upload Plugin</strong>,
        install &amp; activate. Open <strong>☁️ GEO SaaS Sync</strong> in wp-admin and paste an API
        key from above to connect it to this workspace.
      </p>
      <button className="btn-primary" onClick={onDownloadPlugin} disabled={downloading}>
        <Download className="w-4 h-4" />
        {downloading ? 'Preparing…' : 'Download plugin (.zip)'}
      </button>
    </div>
  );
}
